/**
 * Vendor-agnostic product analytics.
 *
 * `track()` is safe to call from anywhere and no-ops until a provider is wired via `initAnalytics`
 * (e.g. PostHog, Mixpanel, Segment), so instrumentation can be added now and a provider chosen
 * later without touching call sites. Analytics must never break the app, so all calls are guarded.
 *
 * Example wiring (in index.js once a provider is chosen):
 *   import posthog from 'posthog-js';
 *   posthog.init(KEY, { api_host: HOST });
 *   initAnalytics({ capture: (e, p) => posthog.capture(e, p), identify: (id, t) => posthog.identify(id, t) });
 */
import { attributionSetOnceProperties } from './attribution';

let provider = null;

// The funnel events attribution should ride along on, so a campaign can be traced all the way
// to activation, see utils/attribution.js for where this comes from.
const ATTRIBUTED_EVENTS = new Set([
  'signup_completed', 'number_connected', 'flow_published', 'upgrade_clicked',
]);

export function initAnalytics(p) {
  provider = p;
}

/**
 * @param {string} userId
 * @param {object} [traits] Regular person properties, overwritten on every call ($set semantics).
 * @param {object} [setOnceTraits] Properties that should only ever be recorded once (PostHog's
 *   $set_once), used for first-touch attribution (see utils/attribution.js) so a later sign-in
 *   from an unattributed session never overwrites the campaign that originally brought them in.
 */
export function identifyUser(userId, traits = {}, setOnceTraits = undefined) {
  try {
    if (setOnceTraits !== undefined) {
      provider?.identify?.(userId, traits, setOnceTraits);
    } else {
      provider?.identify?.(userId, traits);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('analytics identify failed', e);
  }
}

/**
 * Disassociate the current user (call on logout) so subsequent events are anonymous and not
 * attributed to the previous user on a shared device.
 */
export function resetUser() {
  try {
    provider?.reset?.();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('analytics reset failed', e);
  }
}

/**
 * Normalises a flow object into the common property bag shared by every flow lifecycle event
 * (saved / deployed / published / deprecated / deleted / duplicated), so PostHog breakdowns are
 * consistent across the activation funnel. Null-safe, handles flows synced from WhatsApp that
 * carry no local `flowJson`.
 */
export function flowProps(flow = {}) {
  const screens = flow?.flowJson?.screens || [];
  return {
    flowId: flow?.id ?? null,
    status: flow?.status ?? null,
    screenCount: screens.length,
    componentCount: screens.reduce((n, s) => n + (s?.components?.length || 0), 0),
    fromTemplate: !!flow?.fromTemplate,
  };
}

export function track(event, props = {}) {
  try {
    // Attach first-touch attribution to the events that matter for a campaign's ROI, so it
    // never has to be threaded through every call site individually.
    const attribution = ATTRIBUTED_EVENTS.has(event) ? attributionSetOnceProperties() : {};
    const fullProps = { ...props, ...attribution };
    provider?.capture?.(event, fullProps);
    if (!provider && process.env.REACT_APP_ANALYTICS_DEBUG === 'true') {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', event, fullProps);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('analytics track failed', e);
  }
}
